import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAlertActions, useLoadingActions } from "@/store";
import "react-datepicker/dist/react-datepicker.css";
import {
  SignInContainer,
  FormContainer,
  FormWrapper,
  AuthLogoBlock,
  AuthTitleBlock,
  TitleText,
  ButtonWrapper,
  SubmitButton,
  ButtonNoBgWrapper,
  ButtonNoBg,
  ErrorText,
} from "./Auth.elements";
import { doSignInWithEmailAndPassword } from "../../../firebase/auth";
import { TextField } from "@mui/material";
import PasswordField from "@/components/PasswordField";
import { Logo } from "@/components/Logo/Logo";

const SIGN_IN_ERROR_MESSAGES = {
  "auth/user-disabled":
    "This account has been disabled.\nPlease contact your administrator.",
  "auth/too-many-requests":
    "Too many failed attempts — this account is temporarily locked.\nWait a few minutes, or use \"Forgot Password?\" to unlock it by resetting your password.",
  "auth/network-request-failed":
    "Network error.\nPlease check your connection and try again.",
};

const DEFAULT_SIGN_IN_ERROR = "Your email or password are incorrect.\nPlease try again!";

const SignInForm = (props) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ mode: "onSubmit", reValidateMode: "onSubmit" });

  const email = watch("email");
  const password = watch("password");
  const canSubmit = Boolean(email?.trim()) && Boolean(password?.trim());

  const navigate = useNavigate();

  const handleResetPassword = () => {
    navigate("/resetpassword", { replace: true });
  };

  const [currentPasswordError, setCurrentPasswordError] = useState("");

  useEffect(() => {
    if (currentPasswordError) setCurrentPasswordError("");
  }, [email, password]);

  const { showLoading, hideLoading } = useLoadingActions();
  const { hideAlert } = useAlertActions();

  const onSubmit = async (data) => {
    try {
      showLoading();
      hideAlert();
      const userCredential = await doSignInWithEmailAndPassword(
          data.email.trim().toLowerCase(),
          data.password
      );
      console.log("Signed in:", userCredential.user);
      hideLoading();
      navigate("/");
    } catch (error) {
      hideLoading();
      setCurrentPasswordError(
          SIGN_IN_ERROR_MESSAGES[error?.code] || DEFAULT_SIGN_IN_ERROR
      );
      console.error("Error signing in:", error?.code, error);
    }
  };

  return (
      <SignInContainer>
        <FormContainer>
          <AuthLogoBlock>
            <Logo />
          </AuthLogoBlock>
          <AuthTitleBlock>
            <TitleText>SIGN IN</TitleText>
          </AuthTitleBlock>

          {currentPasswordError && <ErrorText>{currentPasswordError}</ErrorText>}

          <form onSubmit={handleSubmit(onSubmit)}>
            <FormWrapper>
              <TextField
                  fullWidth
                  label="Email"
                  placeholder="Enter your email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
                      message: "Invalid email address",
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message || " "}
              />

              <PasswordField
                  label="Password"
                  fieldName="password"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters long",
                    },
                  }}
              />
            </FormWrapper>

            <ButtonWrapper>
              <SubmitButton
                  buttonState="auth"
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
              >
                Sign In
              </SubmitButton>
            </ButtonWrapper>
          </form>

          <ButtonNoBgWrapper>
            <ButtonNoBg onClick={handleResetPassword}>Forgot Password?</ButtonNoBg>
          </ButtonNoBgWrapper>
        </FormContainer>
      </SignInContainer>
  );
};

export default SignInForm;
